// Una lista larga adentro de un contenedor con scroll, dibujada DE A TANDAS
// (ADR-2026-10-21-el-filtro-de-producto-carga-de-a-tandas).
//
// Para las listas de los filtros (los productos de Creaciones): un catálogo puede tener
// cientos de filas y cada una lleva una foto. Se dibujan `porTanda` filas y se suma otra
// tanda cuando el centinela del final entra en el contenedor. `ImagenAlVerse` baja la foto
// recién cuando la fila está por verse: `loading="lazy"` no sirve adentro de un contenedor
// con scroll propio, porque el navegador mide contra la ventana y no contra el contenedor.

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export function useListaPorTandas<T>(
  lista: T[],
  {
    raiz,
    porTanda = 24,
    reiniciarCon,
  }: {
    /** El contenedor que scrollea. */
    raiz: RefObject<HTMLElement>;
    porTanda?: number;
    /** Cuando cambia (abrir la lista, otra búsqueda, otra marca) vuelve a la primera tanda. */
    reiniciarCon: string;
  },
) {
  const [cuantos, setCuantos] = useState(porTanda);
  useEffect(() => setCuantos(porTanda), [reiniciarCon, porTanda]);
  const largoRef = useRef(lista.length);
  largoRef.current = lista.length;

  const observador = useRef<IntersectionObserver | null>(null);
  const centinelaRef = useCallback(
    (nodo: HTMLElement | null) => {
      observador.current?.disconnect();
      observador.current = null;
      if (!nodo || typeof IntersectionObserver === 'undefined') return;
      observador.current = new IntersectionObserver(
        (entradas) => {
          if (entradas.some((e) => e.isIntersecting)) {
            setCuantos((c) => Math.min(c + porTanda, Math.max(largoRef.current, porTanda)));
          }
        },
        { root: raiz.current, rootMargin: '0px 0px 160px 0px' },
      );
      observador.current.observe(nodo);
    },
    [raiz, porTanda],
  );
  useEffect(() => () => observador.current?.disconnect(), []);

  return { visibles: lista.slice(0, cuantos), hayMas: lista.length > cuantos, centinelaRef };
}

/** Una foto que se baja recién cuando entra (o está por entrar) en `raiz`. Hasta entonces,
 *  `relleno`. */
export function ImagenAlVerse({
  src,
  raiz,
  className,
  relleno,
}: {
  src: string;
  raiz: RefObject<HTMLElement>;
  className?: string;
  relleno: React.ReactNode;
}) {
  const [vista, setVista] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (vista || !ref.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVista(true);
      return;
    }
    const io = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          setVista(true);
          io.disconnect();
        }
      },
      { root: raiz.current, rootMargin: '80px 0px' },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [vista, raiz]);
  if (vista) return <img src={src} alt="" decoding="async" className={className} />;
  return (
    // `inline-flex` y no `contents`: sin caja propia el observador nunca lo ve entrar.
    <span ref={ref} className="inline-flex shrink-0">
      {relleno}
    </span>
  );
}
